import React, { useEffect } from "react";
import { ContentMainLayout } from "../../layout/contentLayout";
import { Box, Button, Stack, Typography } from "@mui/material";
import useResponsive from "../../hooks/useResponsive";
import RepresentativeContainer from "../representative-container";
import ReportContainer from "../report-container";
import { useDrawer } from "../../hooks/useDrawer";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import { SkeletonLoading } from "../ui-elements/skeleton-loading";

import { DashboardPropsData } from "../../interface/dashboard";
import { useQuery } from "@tanstack/react-query";
import { apiGetAccount } from "../../api/account";
import {
  AccountProfileData,
  DummyAccountData,
  DummyAccountProfileData,
} from "../../interface/account";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";

const VerifiedContent = ({
  items,
  sentryRef,
  loading,
}: {
  items: DashboardPropsData[];
  sentryRef: any;
  loading: boolean;
}) => {
  const isDesktop = useResponsive("down", "lg", "xl");
  const isSmall = useResponsive("down", "sm", "xl");
  const { openDrawer } = useDrawer();
  const account = useSelector(
    (state: RootState) => state.content.account_profile_data
  );

  const { data: accountProfileDetails, isLoading } = useQuery({
    queryKey: ["account-profile-details", account?.id],
    queryFn: () => apiGetAccount(account?.id),
    enabled: Boolean(account.id),
    staleTime: 1000 * 60 * 10,
    select: (response) => {
      if (response?.status === "error") {
        if ("details" in response) {
          return DummyAccountProfileData;
        }
        return DummyAccountProfileData;
      }
      if (response?.status === 200) {
        return response?.data as AccountProfileData;
      }
      return DummyAccountProfileData;
    },
  });

  return (
    <ContentMainLayout>
      <Stack
        direction={"row"}
        spacing={4}
        justifyContent={isDesktop ? "center" : "space-between"}
      >
        <Stack width={isSmall ? "100%" : 560}>
          <Box
            id="main-scroll-container"
            sx={{
              width: "100%",
              mx: "auto",
              mt: "auto",
              height: "76vh",
              overflowY: "auto",
              pt: "80px",
              overflowX: "hidden",
            }}
          >
            {loading && items?.length === 0 && (
              <Box sx={{}}>
                {[...Array(3)].map((_, idx) => (
                  <SkeletonLoading key={idx} />
                ))}
              </Box>
            )}
            {!loading && items?.length === 0 && (
              <Box
                sx={{
                  height: 400,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="h5" color="black" sx={{ fontWeight: 500 }}>
                  No Data available in your Community
                </Typography>
              </Box>
            )}
            {isDesktop && (
              <Box
                sx={{ position: "fixed", right: 10, width: 80, height: "3rem" }}
              >
                <Button
                  variant="contained"
                  onClick={() =>
                    openDrawer(
                      <RepresentativeContainer
                        accountProfileDetails={
                          accountProfileDetails ?? DummyAccountProfileData
                        }
                      />
                    )
                  }
                >
                  <PeopleOutlineIcon />
                </Button>
              </Box>
            )}
            {items?.map((feed, index) => (
              <ReportContainer
                key={index}
                id={feed?.id}
                link={feed?.id}
                content={feed?.context}
                account={feed?.author}
                title={feed?.title}
                source={feed?.post_type}
                badge={feed?.author_badge ?? 0}
                avatarImg={feed?.author_photo_url ?? ""}
                date={feed?.created_at}
                image={feed?.media}
                current_user_bookmarked={feed?.current_user_bookmarked ?? false}
                current_user_liked={feed?.current_user_liked ?? false}
                current_user_reposted={feed?.current_user_reposted ?? false}
                data={feed}
                social={{
                  message: feed?.comments,
                  repost: feed?.reposts,
                  like: feed?.likes,
                }}
              />
            ))}
            <div
              ref={sentryRef}
              style={{
                width: "100%",
                margin: "0 auto",
                height: "20px",
              }}
            >
              {loading && (
                <Box sx={{}}>
                  <SkeletonLoading />
                </Box>
              )}
            </div>
          </Box>
        </Stack>
        <Stack
          width={isDesktop ? "0" : "35%"}
          sx={{
            display: isDesktop ? "none" : "block",
          }}
          height={505}
        >
          <Box
            sx={{
              width: "100%",
              pt: "80px",
              height: "76vh",
            }}
          >
            <RepresentativeContainer
              accountProfileDetails={
                accountProfileDetails ?? DummyAccountProfileData
              }
            />
          </Box>
        </Stack>
      </Stack>
    </ContentMainLayout>
  );
};

export default VerifiedContent;
